import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { BookOpen, Clock, Users, CreditCard, AlertCircle, XCircle, CheckCircle } from 'lucide-react';

const PolicySection = ({ icon: Icon, title, children }) => (
  <div className="pricing-card !p-6 mb-4">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gold/10 border border-gold/20">
        <Icon size={20} className="text-gold" />
      </div>
      <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h3>
    </div>
    <div className="space-y-3 text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
      {children}
    </div>
  </div>
);

const PolicyPage = () => {
  const { language } = useLanguage();
  const sv = language === 'sv';

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="policy-page">
      {/* Hero */}
      <section className="py-16 islamic-pattern hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'Lektionspolicy' : 'Lesson Policy'}
          </h1>
          <p style={{ color: 'var(--text-secondary)' }} className="max-w-2xl mx-auto">
            {sv
              ? 'Viktiga regler och riktlinjer för lektioner, betalningar och frånvaro.'
              : 'Important rules and guidelines for lessons, payments, and absences.'}
          </p>
        </div>
      </section>

      <div className="gold-divider"></div>

      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Group Structure */}
          <PolicySection icon={Users} title={sv ? 'Gruppstruktur' : 'Group Structure'}>
            <p>{sv
              ? 'Vi prioriterar smågrupper med 2-5 elever per grupp. Detta ger varje elev personlig uppmärksamhet och möjlighet att öva.'
              : 'We prioritize small groups of 2-5 students per group. This gives each student personal attention and practice opportunities.'}</p>
            <div className="mt-3 p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={16} className="text-gold" />
                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{sv ? 'Lektionstid baseras på gruppstorlek:' : 'Lesson duration based on group size:'}</span>
              </div>
              <ul className="ml-6 space-y-1.5">
                <li>{sv ? '2 elever = 60 minuter per lektion' : '2 students = 60 minutes per lesson'}</li>
                <li>{sv ? '3-5 elever = 90 minuter per lektion' : '3-5 students = 90 minutes per lesson'}</li>
              </ul>
            </div>
          </PolicySection>

          {/* Lesson Rules */}
          <PolicySection icon={BookOpen} title={sv ? 'Lektionsregler' : 'Lesson Rules'}>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-gold mt-0.5 flex-shrink-0" />
                <span>{sv ? 'Var redo 5 minuter före lektionens start.' : 'Be ready 5 minutes before lesson start.'}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-gold mt-0.5 flex-shrink-0" />
                <span>{sv ? 'Ha ditt material redo (Koran, anteckningsbok).' : 'Have your materials ready (Quran, notebook).'}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-gold mt-0.5 flex-shrink-0" />
                <span>{sv ? 'Se till att du har en tyst och lugn plats under lektionen.' : 'Ensure you have a quiet and calm place during the lesson.'}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-gold mt-0.5 flex-shrink-0" />
                <span>{sv ? 'Kameran ska vara på för barn och killar. Kvinnor behöver inte ha kameran på.' : 'Camera should be on for children and boys. Women do not need to have the camera on.'}</span>
              </li>
            </ul>
          </PolicySection>

          {/* Payment */}
          <PolicySection icon={CreditCard} title={sv ? 'Betalning' : 'Payment'}>
            <p>{sv
              ? 'Betalning sker månadsvis i förskott. Månadskostnaden beräknas automatiskt baserat på vald kurs, antal lektioner och pris per lektion.'
              : 'Payment is monthly in advance. The monthly cost is automatically calculated based on the chosen course, number of lessons, and price per lesson.'}</p>
            <div className="mt-3 p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              <p className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{sv ? 'Exempel:' : 'Example:'}</p>
              <ul className="space-y-1.5">
                <li>{sv ? 'Quran Online Method: 125 SEK per lektion' : 'Quran Online Method: 125 SEK per lesson'}</li>
                <li>{sv ? 'Tajweed: 135 SEK per lektion' : 'Tajweed: 135 SEK per lesson'}</li>
                <li className="pt-1 font-medium" style={{ color: 'var(--text-primary)' }}>
                  {sv ? '8 lektioner/månad x 125 SEK = 1 000 SEK/månad' : '8 lessons/month x 125 SEK = 1,000 SEK/month'}
                </li>
              </ul>
            </div>
            <p className="mt-3">
              {sv
                ? 'Betalning hanteras manuellt av läraren. Du får betalningsinstruktioner efter att du blivit placerad i en grupp.'
                : 'Payment is handled manually by the teacher. You will receive payment instructions after being placed in a group.'}
            </p>
          </PolicySection>

          {/* Schedule */}
          <PolicySection icon={Clock} title={sv ? 'Schema & Tider' : 'Schedule & Times'}>
            <p>{sv
              ? 'Lektionstider bestäms i samråd med gruppen. Läraren meddelar schemat när gruppen är fullsatt.'
              : 'Lesson times are determined in consultation with the group. The teacher will announce the schedule when the group is full.'}</p>
            <ul className="mt-2 space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-gold mt-0.5 flex-shrink-0" />
                <span>{sv ? 'Antal lektioner per vecka väljs vid bokning.' : 'Number of lessons per week is chosen when booking.'}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-gold mt-0.5 flex-shrink-0" />
                <span>{sv ? 'Schemaändringar meddelas minst 24 timmar i förväg.' : 'Schedule changes are announced at least 24 hours in advance.'}</span>
              </li>
            </ul>
          </PolicySection>

          {/* Absence */}
          <PolicySection icon={AlertCircle} title={sv ? 'Frånvaro' : 'Absence'}>
            <p>{sv
              ? 'Vi förstår att livet ibland kommer emellan. Så här hanterar vi frånvaro:'
              : 'We understand that life sometimes gets in the way. Here is how we handle absence:'}</p>
            <ul className="mt-2 space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-gold mt-0.5 flex-shrink-0" />
                <span>{sv
                  ? 'Vid akut hinder — meddela oss så snart som möjligt, vi tar hänsyn och bokar om lektionen.'
                  : 'In case of urgent obstacles — notify us as soon as possible, we will accommodate and reschedule.'}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-gold mt-0.5 flex-shrink-0" />
                <span>{sv
                  ? 'Vid sjukdom — meddela på morgonen så snart du vet att du är sjuk, så bokar vi om.'
                  : 'In case of illness — notify us in the morning as soon as you know you are sick, and we will reschedule.'}</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle size={16} className="text-yellow-400 mt-0.5 flex-shrink-0" />
                <span>{sv
                  ? 'Utebliven frånvaro utan meddelande räknas som en genomförd lektion.'
                  : 'No-show without notice counts as a completed lesson.'}</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle size={16} className="text-yellow-400 mt-0.5 flex-shrink-0" />
                <span>{sv
                  ? 'Upprepade frånvaron utan giltig anledning kan leda till omplacering eller uppsägning.'
                  : 'Repeated absences without valid reason may lead to reassignment or termination.'}</span>
              </li>
            </ul>
          </PolicySection>

          {/* Cancellation */}
          <PolicySection icon={XCircle} title={sv ? 'Avbokning & Uppsägning' : 'Cancellation'}>
            <p>{sv
              ? 'Om du vill avsluta din plats i gruppen, meddela läraren minst en vecka i förväg. Redan betald månad återbetalas ej.'
              : 'If you wish to cancel your spot in the group, notify the teacher at least one week in advance. Already paid months are non-refundable.'}</p>
          </PolicySection>
        </div>
      </section>
    </div>
  );
};

export default PolicyPage;
